import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { UserClaims, AuthService } from '@anecdotes/auth-core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CommentDraft, ThreadViewModel } from '../../models';
import {
  CommentingFacadeService,
  CommentPanelManagerService,
  ConfirmationModalService,
} from '../../services';
import { CommentInputValue, UserModel } from '@anecdotes/commenting';
import { commentCreateDraftLocalStorageKey } from '../../constants';
import { SavableCommentInputComponent } from '../savable-comment-input/savable-comment-input.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WindowHelperService } from '@anecdotes/utils';

@Component({
  selector: 'app-thread-creation',
  templateUrl: './thread-creation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreadCreationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('input', { static: true })
  private input: SavableCommentInputComponent;

  @HostBinding('attr.data-test-id')
  private readonly dataTestId = 'thread-creation-form';

  @HostBinding('class')
  private readonly classes = 'flex flex-col rounded-md border border-solid border-navy-90 border-opacity-30 overflow-hidden bg-white';

  creationInProgress$ = new BehaviorSubject<boolean>(false);

  @Input()
  resourceToCreateThreadFor: ThreadViewModel;

  /**
   * Emits thread id of created thread
   */
  @Output()
  created = new EventEmitter<string>();

  textControl = new UntypedFormControl(null, Validators.required);
  localStorageKey = commentCreateDraftLocalStorageKey;
  currentUser$: Observable<UserClaims>;
  usersMap$: Observable<Map<string, UserModel>>;

  get draftMessage(): string {
    if (this.localStorageKey) {
      const commentDraft: string = this.windowHelper.getWindow().localStorage.getItem(this.localStorageKey);
      return commentDraft ? (JSON.parse(commentDraft) as CommentDraft)?.body : null;
    }
  }

  constructor(
    private commentingFacadeService: CommentingFacadeService,
    private commentPanelManager: CommentPanelManagerService,
    private authService: AuthService,
    private cd: ChangeDetectorRef,
    private windowHelper: WindowHelperService,
    private confirmationService: ConfirmationModalService
  ) {}

  ngOnInit(): void {
    this.usersMap$ = this.commentPanelManager.getUsersMap();
    this.currentUser$ = this.authService.getUser();
    this.textControl.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cd.detectChanges());
  }

  async commentClick(): Promise<void> {
    try {
      if (await this.confirmationService.openAuditorMentionModal(this.textControl.value?.body)) {
        this.creationInProgress$.next(true);
        const createdThread = await this.commentingFacadeService.createThreadAsync(
          this.resourceToCreateThreadFor.resourceType,
          this.resourceToCreateThreadFor.resourceId,
          this.textControl.value as CommentInputValue
        );
        this.created.emit(createdThread.id);
        this.input.reset();
        this.commentPanelManager.closeCreation();
      }
    } catch (error) {
      // TODO
    } finally {
      this.creationInProgress$.next(false);
    }
  }

  cancelClick(): void {
    this.commentPanelManager.closeCreation();
    this.input.reset();
  }

  buildTranslationKey(relativeKey: string): string {
    return `commenting.${relativeKey}`;
  }
}
